import React, { useState, useEffect } from 'react';
import { Play, Plus, Trash2, Eye, EyeOff, ArrowLeft, Edit2, Save, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { useAuth0UserId } from '../hooks/useAuth0UserId';
import * as promptApi from '../services/promptApi';

interface Variable {
  key: string;
  value: string;
}

interface LLMNodeConfig {
  nodeType: 'llm_qualifier' | 'IntentLLM' | 'llm_case';
  nodeName: string; // Nome identificativo del nodo (es: "llm_qualifier - Agenzia Immobiliare")
  label: string;
  // Common fields
  prompt: string;
  fixed_prompt?: string; // Prompt fisso che viene sempre concatenato
  // API Parameters
  model?: string;
  temperature?: number;
  max_tokens?: number;
  // llm_qualifier specific
  input_variable?: string;
  output_variable?: string;
  decision_variable?: string;
  max_interactions?: number;
  // IntentLLM specific
  intents_variable?: string;
  intent_variable?: string;
  intents?: string[];
  // LLM_CASE specific
  variable?: string;
  cases?: Array<{ test: string }>;
}

interface TestResult {
  input: any;
  apiCall: {
    url: string;
    model: string;
    temperature: number;
    max_tokens?: number;
    messages: any[];
  };
  apiResponse: any;
  output: any;
  updatedVariables: Record<string, any>;
  error?: string;
}

interface SavedPrompt {
  id: string;
  name: string;
  content: string;
  createdAt: string;
}

const NodeTesting: React.FC = () => {
  const navigate = useNavigate();

  // Context variables (simulate previous nodes)
  const [variables, setVariables] = useState<Variable[]>([
    { key: 'INPUT', value: '' },
    { key: 'user_input', value: '' }
  ]);

  // Auth0 user ID
  const userId = useAuth0UserId();

  // Saved node configurations (per caricare nodi salvati)
  const [savedNodes, setSavedNodes] = useState<Array<{ id: string; nodeType: string; nodeName: string; label: string; is_visible_in_studio?: boolean }>>([]);
  const [isSavingFixedPrompt, setIsSavingFixedPrompt] = useState(false);
  const [isVisibleInStudio, setIsVisibleInStudio] = useState(false);

  // LLM Node configuration
  const [nodeConfig, setNodeConfig] = useState<LLMNodeConfig>({
    nodeType: 'llm_qualifier',
    nodeName: '', // Nome identificativo del nodo
    label: 'Test LLM Node',
    prompt: '',
    fixed_prompt: `IMPORTANTE: Per ogni risposta, devi fornire DUE elementi:
1. Una risposta testuale amichevole e conversazionale per l'utente
2. Un JSON con una decisione Yes/No che indica se l'utente vuole procedere operativamente

FORMATO RISPOSTA OBBLIGATORIO:
[Risposta testuale per l'utente]

<JSON_DECISION>
{"decision": "Yes"}
</JSON_DECISION>

oppure

[Risposta testuale per l'utente]

<JSON_DECISION>
{"decision": "No"}
</JSON_DECISION>`,
    model: 'openai/gpt-oss-120b',
    temperature: 0.7,
    max_tokens: 500,
    input_variable: 'user_input',
    output_variable: 'llm_response',
    decision_variable: 'llm_decision',
    max_interactions: 5
  });

  // Test results
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [showApiDetails, setShowApiDetails] = useState(true);

  // Prompt management
  const [savedPrompts, setSavedPrompts] = useState<SavedPrompt[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<string>('');
  const [showPromptManager, setShowPromptManager] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<SavedPrompt | null>(null);
  const [newPromptName, setNewPromptName] = useState('');
  const [newPromptContent, setNewPromptContent] = useState('');

  // Add variable
  const addVariable = () => {
    setVariables([...variables, { key: '', value: '' }]);
  };

  // Remove variable
  const removeVariable = (index: number) => {
    setVariables(variables.filter((_, i) => i !== index));
  };

  // Update variable
  const updateVariable = (index: number, field: 'key' | 'value', value: string) => {
    const newVars = [...variables];
    newVars[index][field] = value;
    setVariables(newVars);
  };

  // Add case (for LLM_CASE)
  const addCase = () => {
    setNodeConfig({
      ...nodeConfig,
      cases: [...(nodeConfig.cases || []), { test: '' }]
    });
  };

  // Remove case
  const removeCase = (index: number) => {
    setNodeConfig({
      ...nodeConfig,
      cases: nodeConfig.cases?.filter((_, i) => i !== index)
    });
  };

  // Update case
  const updateCase = (index: number, value: string) => {
    const newCases = [...(nodeConfig.cases || [])];
    newCases[index].test = value;
    setNodeConfig({ ...nodeConfig, cases: newCases });
  };

  // Load prompts from database (or localStorage fallback)
  useEffect(() => {
    loadPrompts();
    loadSavedNodes();
  }, [userId]);

  // Load prompts
  const loadPrompts = async () => {
    if (!userId) {
      // Fallback a localStorage se non autenticato
      const stored = localStorage.getItem('nodeTesting_prompts');
      if (stored) {
        try {
          setSavedPrompts(JSON.parse(stored));
        } catch (e) {
          console.error('Error loading prompts:', e);
        }
      }
      return;
    }

    try {
      const result = await promptApi.listPrompts(userId, {
        node_type: 'NodeTesting',
        limit: 100
      });
      
      // Filtra solo i prompt personalizzati (non fixed_prompt)
      const customPrompts = result.prompts
        .filter(p => !p.metadata?.is_fixed_prompt)
        .map(p => ({
          id: p.id,
          name: p.name,
          content: p.content,
          createdAt: p.created_at
        }));
      
      setSavedPrompts(customPrompts);
    } catch (error) {
      console.error('Error loading prompts from database:', error);
      // Fallback a localStorage
      const stored = localStorage.getItem('nodeTesting_prompts');
      if (stored) {
        try {
          setSavedPrompts(JSON.parse(stored));
        } catch (e) {
          setSavedPrompts([]);
        }
      }
    }
  };

  // Load saved nodes (per fixed_prompt)
  const loadSavedNodes = async () => {
    if (!userId) return;

    try {
      const result = await promptApi.listPrompts(userId, {
        node_type: 'NodeTesting',
        limit: 100
      });
      
      // Filtra solo i fixed_prompt e crea lista nodi
      const fixedPrompts = result.prompts.filter(p => p.metadata?.is_fixed_prompt);
      const nodes = fixedPrompts.map(p => ({
        id: p.id,
        nodeType: p.metadata?.node_type || '',
        nodeName: p.metadata?.node_name || p.name,
        label: p.name,
        is_visible_in_studio: p.metadata?.is_visible_in_studio || false
      }));
      
      setSavedNodes(nodes);
    } catch (error) {
      console.error('Error loading saved nodes:', error);
    }
  };

  // Carica fixed_prompt quando cambia nodeName o nodeType
  useEffect(() => {
    if (nodeConfig.nodeName && nodeConfig.nodeType && userId) {
      loadNodeFixedPrompt(nodeConfig.nodeType, nodeConfig.nodeName);
    }
  }, [nodeConfig.nodeName, nodeConfig.nodeType, userId]);

  // Save prompts to database (or localStorage fallback)
  const savePrompts = async (prompts: SavedPrompt[]) => {
    if (!userId) {
      // Fallback a localStorage se non autenticato
      localStorage.setItem('nodeTesting_prompts', JSON.stringify(prompts));
      setSavedPrompts(prompts);
      return;
    }

    // Per ora manteniamo localStorage come backup
    localStorage.setItem('nodeTesting_prompts', JSON.stringify(prompts));
    setSavedPrompts(prompts);
  };

  // Select prompt from dropdown
  const handleSelectPrompt = (promptId: string) => {
    setSelectedPromptId(promptId);
    const prompt = savedPrompts.find(p => p.id === promptId);
    if (prompt) {
      setNodeConfig({ ...nodeConfig, prompt: prompt.content });
    }
  };

  // Create new prompt
  const handleCreatePrompt = async () => {
    if (!newPromptName.trim() || !newPromptContent.trim()) {
      alert('❌ Inserisci nome e contenuto del prompt!');
      return;
    }

    if (!userId) {
      // Fallback a localStorage
      const newPrompt: SavedPrompt = {
        id: Date.now().toString(),
        name: newPromptName.trim(),
        content: newPromptContent.trim(),
        createdAt: new Date().toISOString()
      };
      const updated = [...savedPrompts, newPrompt];
      savePrompts(updated);
      setNewPromptName('');
      setNewPromptContent('');
      setShowPromptManager(false);
      alert('✅ Prompt salvato!');
      return;
    }

    try {
      const created = await promptApi.createPrompt(
        {
          node_type: 'NodeTesting',
          name: newPromptName.trim(),
          content: newPromptContent.trim(),
          metadata: { is_fixed_prompt: false }
        },
        userId
      );

      const newPrompt: SavedPrompt = {
        id: created.id,
        name: created.name,
        content: created.content,
        createdAt: created.created_at
      };
      
      setSavedPrompts([...savedPrompts, newPrompt]);
      setNewPromptName('');
      setNewPromptContent('');
      setShowPromptManager(false);
      alert('✅ Prompt salvato nel database!');
    } catch (error: any) {
      console.error('Error saving prompt:', error);
      alert(`❌ Errore salvataggio: ${error.message}`);
    }
  };

  // Edit prompt
  const handleEditPrompt = (prompt: SavedPrompt) => {
    setEditingPrompt(prompt);
    setNewPromptName(prompt.name);
    setNewPromptContent(prompt.content);
  };

  // Update prompt
  const handleUpdatePrompt = async () => {
    if (!editingPrompt || !newPromptName.trim() || !newPromptContent.trim()) {
      alert('❌ Inserisci nome e contenuto del prompt!');
      return;
    }

    if (!userId) {
      // Fallback a localStorage
      const updated = savedPrompts.map(p =>
        p.id === editingPrompt.id
          ? { ...p, name: newPromptName.trim(), content: newPromptContent.trim() }
          : p
      );
      savePrompts(updated);
      setEditingPrompt(null);
      setNewPromptName('');
      setNewPromptContent('');
      setShowPromptManager(false);
      alert('✅ Prompt aggiornato!');
      return;
    }

    try {
      await promptApi.updatePrompt(
        editingPrompt.id,
        {
          name: newPromptName.trim(),
          content: newPromptContent.trim()
        },
        userId
      );

      const updated = savedPrompts.map(p =>
        p.id === editingPrompt.id
          ? { ...p, name: newPromptName.trim(), content: newPromptContent.trim() }
          : p
      );
      setSavedPrompts(updated);
      setEditingPrompt(null);
      setNewPromptName('');
      setNewPromptContent('');
      setShowPromptManager(false);
      alert('✅ Prompt aggiornato nel database!');
    } catch (error: any) {
      console.error('Error updating prompt:', error);
      alert(`❌ Errore aggiornamento: ${error.message}`);
    }
  };

  // Delete prompt
  const handleDeletePrompt = async (promptId: string) => {
    if (!confirm('⚠️ Sei sicuro di voler eliminare questo prompt?')) return;

    if (!userId) {
      // Fallback a localStorage
      const updated = savedPrompts.filter(p => p.id !== promptId);
      savePrompts(updated);
      if (selectedPromptId === promptId) {
        setSelectedPromptId('');
        setNodeConfig({ ...nodeConfig, prompt: '' });
      }
      alert('✅ Prompt eliminato!');
      return;
    }

    try {
      await promptApi.deletePrompt(promptId, userId);
      const updated = savedPrompts.filter(p => p.id !== promptId);
      setSavedPrompts(updated);
      if (selectedPromptId === promptId) {
        setSelectedPromptId('');
        setNodeConfig({ ...nodeConfig, prompt: '' });
      }
      alert('✅ Prompt eliminato!');
    } catch (error: any) {
      console.error('Error deleting prompt:', error);
      alert(`❌ Errore eliminazione: ${error.message}`);
    }
  };

  // Save fixed_prompt to database
  const saveFixedPrompt = async () => {
    if (!nodeConfig.nodeName.trim()) {
      alert('❌ Inserisci prima un nome per il nodo!');
      return;
    }

    if (!userId) {
      alert('⚠️ Devi essere autenticato per salvare il prompt fisso nel database.');
      return;
    }

    setIsSavingFixedPrompt(true);
    try {
      const promptName = `${nodeConfig.nodeType} - ${nodeConfig.nodeName}`;
      
      // Cerca se esiste già un fixed_prompt per questo nodo
      const existing = savedNodes.find(
        n => n.nodeType === nodeConfig.nodeType && n.nodeName === nodeConfig.nodeName
      );

      const fixedPromptData = {
        node_type: 'NodeTesting',
        name: promptName,
        content: nodeConfig.fixed_prompt || '',
        metadata: {
          is_fixed_prompt: true,
          node_type: nodeConfig.nodeType,
          node_name: nodeConfig.nodeName,
          // Salva anche il prompt variabile nel metadata
          variable_prompt: nodeConfig.prompt || '',
          // Salva TUTTI i parametri del nodo per ripristinarli completamente
          intents: nodeConfig.intents || [],
          max_interactions: nodeConfig.max_interactions,
          model: nodeConfig.model,
          temperature: nodeConfig.temperature,
          max_tokens: nodeConfig.max_tokens,
          input_variable: nodeConfig.input_variable,
          output_variable: nodeConfig.output_variable,
          intents_variable: nodeConfig.intents_variable,
          intent_variable: nodeConfig.intent_variable,
          decision_variable: nodeConfig.decision_variable,
          label: nodeConfig.label,
          // Flag per visibilità in FlowStudio - SEMPRE incluso (true o false, mai null)
          is_visible_in_studio: Boolean(isVisibleInStudio)
        }
      };
      
      // Log per debug
      console.log('💾 Salvataggio nodo con metadata:', {
        nodeName: promptName,
        isVisibleInStudio: isVisibleInStudio,
        isVisibleInStudioBoolean: Boolean(isVisibleInStudio),
        fullMetadata: fixedPromptData.metadata
      });

      if (existing) {
        // Aggiorna esistente
        await promptApi.updatePrompt(existing.id, fixedPromptData, userId);
        console.log('✅ Nodo aggiornato:', {
          id: existing.id,
          name: promptName,
          is_visible_in_studio: isVisibleInStudio,
          metadata: fixedPromptData.metadata
        });
        alert(`✅ Prompt fisso e variabile salvati nel database!\n\n${isVisibleInStudio ? '⭐ Il nodo è VISIBILE in FlowStudio' : '⚠️ Il nodo NON è visibile in FlowStudio (deseleziona il checkbox per nasconderlo)'}`);
      } else {
        // Crea nuovo
        const created = await promptApi.createPrompt(fixedPromptData, userId);
        console.log('✅ Nodo creato:', {
          id: created?.id,
          name: promptName,
          is_visible_in_studio: isVisibleInStudio,
          metadata: fixedPromptData.metadata
        });
        alert(`✅ Prompt fisso e variabile salvati nel database!\n\n${isVisibleInStudio ? '⭐ Il nodo è VISIBILE in FlowStudio' : '⚠️ Il nodo NON è visibile in FlowStudio (seleziona il checkbox per mostrarlo)'}`);
      }

      // Ricarica lista nodi
      await loadSavedNodes();
    } catch (error: any) {
      console.error('Error saving fixed prompt:', error);
      alert(`❌ Errore salvataggio: ${error.message}`);
    } finally {
      setIsSavingFixedPrompt(false);
    }
  };

  // Delete saved node
  const handleDeleteSavedNode = async (nodeId: string) => {
    if (!confirm('⚠️ Sei sicuro di voler eliminare questo nodo salvato?')) return;

    if (!userId) {
      alert('⚠️ Devi essere autenticato per eliminare nodi salvati.');
      return;
    }

    try {
      await promptApi.deletePrompt(nodeId, userId);
      alert('✅ Nodo eliminato con successo!');
      await loadSavedNodes();
    } catch (error: any) {
      console.error('Error deleting node:', error);
      alert(`❌ Errore eliminazione: ${error.message}`);
    }
  };

  // Toggle visibility in Studio
  const handleToggleVisibility = async (nodeId: string, currentVisibility: boolean) => {
    if (!userId) return;

    try {
      const node = savedNodes.find(n => n.id === nodeId);
      if (!node) return;

      // Carica il prompt completo per aggiornarlo
      const result = await promptApi.listPrompts(userId, {
        node_type: 'NodeTesting',
        limit: 100
      });

      const fixedPrompt = result.prompts.find(p => p.id === nodeId);
      if (!fixedPrompt) return;

      const updatedMetadata = {
        ...fixedPrompt.metadata,
        is_visible_in_studio: !currentVisibility
      };

      await promptApi.updatePrompt(nodeId, {
        metadata: updatedMetadata
      }, userId);

      await loadSavedNodes();
    } catch (error: any) {
      console.error('Error toggling visibility:', error);
      alert(`❌ Errore aggiornamento: ${error.message}`);
    }
  };

  // Load fixed_prompt and variable prompt from database when node is selected
  const loadNodeFixedPrompt = async (nodeType: string, nodeName: string) => {
    if (!userId || !nodeName.trim()) return;

    try {
      const result = await promptApi.listPrompts(userId, {
        node_type: 'NodeTesting',
        limit: 100
      });

      const fixedPrompt = result.prompts.find(
        p => p.metadata?.is_fixed_prompt &&
             p.metadata?.node_type === nodeType &&
             p.metadata?.node_name === nodeName
      );

      if (fixedPrompt) {
        setNodeConfig(prev => ({
          ...prev,
          fixed_prompt: fixedPrompt.content,
          // Carica anche il prompt variabile dal metadata se presente
          prompt: fixedPrompt.metadata?.variable_prompt || prev.prompt
        }));
        // Carica anche il flag is_visible_in_studio
        setIsVisibleInStudio(fixedPrompt.metadata?.is_visible_in_studio || false);
      }
    } catch (error) {
      console.error('Error loading fixed prompt:', error);
    }
  };

  // Execute test
  const executeTest = async () => {
    setIsExecuting(true);
    setTestResult(null);

    try {
      // Prepare request body
      const contextVars: Record<string, string> = {};
      variables.forEach(v => {
        if (v.key) contextVars[v.key] = v.value;
      });

      // Concatena prompt personalizzato con prompt fisso
      const customPrompt = nodeConfig.prompt || '';
      const fixedPrompt = nodeConfig.fixed_prompt || '';
      
      // Costruisci il prompt completo
      let fullPrompt = customPrompt;
      if (fixedPrompt) {
        if (customPrompt) {
          fullPrompt = `${customPrompt}\n\n${fixedPrompt}`;
        } else {
          fullPrompt = fixedPrompt;
        }
      }

      // Per LLM_CASE, sostituisci {cases_description} con i casi reali
      if (nodeConfig.nodeType === 'llm_case' && nodeConfig.cases && nodeConfig.cases.length > 0) {
        const casesDescription = nodeConfig.cases
          .map((c, i) => `${i + 1}. ${c.test}`)
          .join('\n');
        fullPrompt = fullPrompt.replace('{cases_description}', casesDescription);
      }

      // Assicurati che il prompt completo sia incluso nel nodeConfig
      const configToSend = {
        ...nodeConfig,
        prompt: fullPrompt,
        // Non inviare fixed_prompt separatamente, è già incluso in prompt
        fixed_prompt: undefined
      };

      console.log('🔍 Invio test con configurazione:', {
        nodeType: configToSend.nodeType,
        customPrompt: customPrompt.substring(0, 100),
        fixedPrompt: fixedPrompt.substring(0, 100),
        fullPromptLength: fullPrompt.length
      });

      const response = await fetch(`${API_URL}/api/test-node`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nodeType: configToSend.nodeType,
          nodeConfig: configToSend,
          contextVariables: contextVars
        })
      });

      const result = await response.json();
      setTestResult(result);
    } catch (error: any) {
      setTestResult({
        input: {},
        apiCall: { url: '', model: '', temperature: 0, messages: [] },
        apiResponse: null,
        output: null,
        updatedVariables: {},
        error: error.message
      });
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with back button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition"
          >
            <ArrowLeft size={20} />
            <span>Torna al Flow Studio</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Node Testing Workbench</h1>
          <p className="text-gray-600">Test e debug dei nodi LLM in isolamento</p>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* LEFT COLUMN: Configuration */}
          <div className="space-y-6">
            {/* Context Variables */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Variabili di Contesto</h2>
                <button
                  onClick={addVariable}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                >
                  <Plus size={16} />
                  Aggiungi
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Simula le variabili provenienti da nodi precedenti
              </p>
              <div className="space-y-2">
                {variables.map((variable, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={variable.key}
                      onChange={(e) => updateVariable(index, 'key', e.target.value)}
                      placeholder="Nome variabile"
                      className="w-1/3 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={variable.value}
                      onChange={(e) => updateVariable(index, 'value', e.target.value)}
                      placeholder="Valore"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => removeVariable(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Node Configuration */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Configurazione Nodo</h2>

              {/* Nome Nodo */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Nodo <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={nodeConfig.nodeName}
                    onChange={(e) => {
                      setNodeConfig({ ...nodeConfig, nodeName: e.target.value });
                    }}
                    placeholder="es: Agenzia Immobiliare, Centro Benessere..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {savedNodes.length > 0 && (
                    <select
                      onChange={async (e) => {
                        const selected = savedNodes.find(n => n.id === e.target.value);
                        if (selected) {
                          // Aggiorna nodeType, nodeName e label
                          setNodeConfig(prev => ({
                            ...prev,
                            nodeType: selected.nodeType as 'llm_qualifier' | 'IntentLLM' | 'llm_case',
                            nodeName: selected.nodeName,
                            label: selected.label
                          }));
                          // Carica fixed_prompt e prompt variabile dal database
                          // Questo verrà eseguito automaticamente dall'useEffect quando cambiano nodeType/nodeName
                          // Ma lo chiamiamo anche qui per essere sicuri
                          await loadNodeFixedPrompt(selected.nodeType, selected.nodeName);
                        }
                        // Reset del select dopo la selezione
                        e.target.value = '';
                      }}
                      className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      defaultValue=""
                    >
                      <option value="">Carica nodo salvato...</option>
                      {savedNodes.map(node => (
                        <option key={node.id} value={node.id}>
                          {node.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Nome identificativo per associare prompt fissi diversi allo stesso tipo di nodo
                </p>
              </div>

              {/* Node Type */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo Nodo
                </label>
                <select
                  value={nodeConfig.nodeType}
                  onChange={(e) => {
                    const newType = e.target.value as 'llm_qualifier' | 'IntentLLM' | 'llm_case';
                    // Aggiorna il prompt fisso in base al tipo di nodo (solo se non c'è un nodo salvato)
                    let newFixedPrompt = '';

                    if (newType === 'llm_qualifier') {
                      newFixedPrompt = `IMPORTANTE: Per ogni risposta, devi fornire DUE elementi:
1. Una risposta testuale amichevole e conversazionale per l'utente
2. Un JSON con una decisione Yes/No che indica se l'utente vuole procedere operativamente

FORMATO RISPOSTA OBBLIGATORIO:
[Risposta testuale per l'utente]

<JSON_DECISION>
{"decision": "Yes"}
</JSON_DECISION>

oppure

[Risposta testuale per l'utente]

<JSON_DECISION>
{"decision": "No"}
</JSON_DECISION>`;
                    } else if (newType === 'IntentLLM') {
                      newFixedPrompt = `IMPORTANTE: Per ogni risposta, devi fornire DUE elementi:
1. Una risposta testuale naturale e conversazionale per l'utente
2. Un JSON con lo stato di rilevazione di TUTTE le intenzioni

FORMATO RISPOSTA OBBLIGATORIO:
[Risposta testuale per l'utente]

<INTENTS_JSON>
{
  "intents": {
    "comprare": true/false,
    "affittare": true/false,
    "vendere": true/false,
    "info": true/false
  },
  "detected_intent": "nome_intenzione" o null,
  "has_intent": true/false
}
</INTENTS_JSON>

REGOLE PER LA RILEVAZIONE:
- Imposta un'intenzione a "true" SOLO se l'utente esprime CHIARAMENTE quella specifica intenzione
- Puoi impostare più intenzioni a "true" se l'utente esprime interesse per più cose
- "detected_intent" deve contenere il NOME della PRIMA intenzione rilevata (se presente), altrimenti null
- "has_intent" deve essere true se ALMENO UNA intenzione è stata rilevata, altrimenti false`;
                    } else {
                      newFixedPrompt = `Analizza l'input dell'utente e determina quale caso corrisponde meglio.

CASI DISPONIBILI:
{cases_description}

Rispondi SOLO con un JSON nel formato:
{"matched_case": <numero>}

Dove <numero> è il numero del caso (1, 2, 3, ...) che corrisponde meglio all'input.
Se nessun caso corrisponde, usa 0.`;
                    }

                    // Aggiorna anche i parametri API in base al tipo di nodo
                    let defaultApiParams;
                    if (newType === 'llm_qualifier' || newType === 'IntentLLM') {
                      defaultApiParams = {
                        model: 'openai/gpt-oss-120b',
                        temperature: 0.7,
                        max_tokens: 500
                      };
                    } else {
                      defaultApiParams = {
                        model: 'llama-3.3-70b-versatile',
                        temperature: 0.1,
                        max_tokens: 200
                      };
                    }

                    // Usa i valori esistenti se presenti, altrimenti i default
                    const newApiParams = {
                      model: nodeConfig.model || defaultApiParams.model,
                      temperature: nodeConfig.temperature ?? defaultApiParams.temperature,
                      max_tokens: nodeConfig.max_tokens ?? defaultApiParams.max_tokens
                    };

                    // Aggiungi campi specifici per IntentLLM
                    const additionalFields = newType === 'IntentLLM' ? {
                      intents: ['comprare', 'affittare', 'vendere', 'info'],
                      intents_variable: 'detected_intents'
                    } : {};

                    // Se c'è un nodeName, prova a caricare il fixed_prompt salvato
                    // Altrimenti usa il default
                    const updatedConfig = {
                      ...nodeConfig,
                      nodeType: newType,
                      ...newApiParams,
                      ...additionalFields
                    };

                    // Se c'è un nodeName, il fixed_prompt verrà caricato dall'useEffect
                    // Altrimenti usa il default
                    if (!nodeConfig.nodeName) {
                      updatedConfig.fixed_prompt = newFixedPrompt;
                    }

                    setNodeConfig(updatedConfig);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="llm_qualifier">LLM_QUALIFIER (Conversational)</option>
                  <option value="IntentLLM">INTENT_LLM (Intent Detection)</option>
                  <option value="llm_case">LLM_CASE (Routing)</option>
                </select>
              </div>

              {/* Label */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Label
                </label>
                <input
                  type="text"
                  value={nodeConfig.label}
                  onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Prompt */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Prompt
                  </label>
                  <button
                    onClick={() => {
                      setShowPromptManager(true);
                      setEditingPrompt(null);
                      setNewPromptName('');
                      setNewPromptContent('');
                    }}
                    className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                    title="Gestisci prompt salvati"
                  >
                    <Edit2 size={14} />
                    Gestisci
                  </button>
                </div>
                <div className="flex gap-2 mb-2">
                  <select
                    value={selectedPromptId}
                    onChange={(e) => handleSelectPrompt(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">-- Seleziona prompt salvato --</option>
                    {savedPrompts.map(prompt => (
                      <option key={prompt.id} value={prompt.id}>
                        {prompt.name}
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={nodeConfig.prompt}
                  onChange={(e) => setNodeConfig({ ...nodeConfig, prompt: e.target.value })}
                  rows={6}
                  placeholder="Inserisci il prompt personalizzato per il nodo LLM..."
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
              </div>

              {/* Prompt Fisso */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Prompt Fisso
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded border border-blue-200">
                      <input
                        type="checkbox"
                        id="visibleInStudio"
                        checked={isVisibleInStudio}
                        onChange={(e) => setIsVisibleInStudio(e.target.checked)}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <label htmlFor="visibleInStudio" className="text-sm font-medium text-gray-700 cursor-pointer whitespace-nowrap">
                        ⭐ Visibile in Studio
                      </label>
                    </div>
                    <button
                      onClick={saveFixedPrompt}
                      disabled={!nodeConfig.nodeName.trim() || isSavingFixedPrompt || !userId}
                      className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-xs font-semibold"
                      title={!nodeConfig.nodeName.trim() ? 'Inserisci prima un nome per il nodo' : !userId ? 'Devi essere autenticato' : 'Salva prompt fisso nel database'}
                    >
                      <Save size={14} />
                      {isSavingFixedPrompt ? 'Salvataggio...' : 'Salva nel DB'}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Questo prompt viene sempre concatenato al prompt personalizzato sopra. Modificalo se necessario.
                  {userId ? ' Il prompt fisso viene salvato nel database associato al nome del nodo.' : ' ⚠️ Autenticati per salvare nel database.'}
                  {isVisibleInStudio && ' ✅ Questo nodo sarà visibile nella palette di FlowStudio.'}
                </p>
                <textarea
                  value={nodeConfig.fixed_prompt || ''}
                  onChange={(e) => setNodeConfig({ ...nodeConfig, fixed_prompt: e.target.value })}
                  rows={8}
                  placeholder="Prompt fisso che viene sempre aggiunto..."
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm bg-gray-50"
                />
              </div>

              {/* API Parameters */}
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <label className="block text-sm font-semibold text-blue-900 mb-3">
                  ⚙️ Parametri API
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Modello
                    </label>
                    <input
                      type="text"
                      value={nodeConfig.model || ''}
                      onChange={(e) => setNodeConfig({ ...nodeConfig, model: e.target.value })}
                      placeholder="es: openai/gpt-oss-120b"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Temperatura: <span className="font-bold text-blue-600">{nodeConfig.temperature ?? 0.7}</span>
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={nodeConfig.temperature ?? 0.7}
                        onChange={(e) => setNodeConfig({ ...nodeConfig, temperature: parseFloat(e.target.value) })}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="2"
                        value={nodeConfig.temperature ?? 0.7}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val) && val >= 0 && val <= 2) {
                            setNodeConfig({ ...nodeConfig, temperature: val });
                          }
                        }}
                        className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-center"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      0 = deterministico, 2 = molto creativo
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Max Tokens
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="8192"
                      value={nodeConfig.max_tokens ?? 500}
                      onChange={(e) => setNodeConfig({ ...nodeConfig, max_tokens: parseInt(e.target.value) || 500 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* llm_qualifier specific fields */}
              {nodeConfig.nodeType === 'llm_qualifier' && (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Input Variable
                      </label>
                      <input
                        type="text"
                        value={nodeConfig.input_variable}
                        onChange={(e) => setNodeConfig({ ...nodeConfig, input_variable: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Interactions
                      </label>
                      <input
                        type="number"
                        value={nodeConfig.max_interactions}
                        onChange={(e) => setNodeConfig({ ...nodeConfig, max_interactions: parseInt(e.target.value) })}
                        min={1}
                        max={20}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Output Variable
                      </label>
                      <input
                        type="text"
                        value={nodeConfig.output_variable}
                        onChange={(e) => setNodeConfig({ ...nodeConfig, output_variable: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Decision Variable
                      </label>
                      <input
                        type="text"
                        value={nodeConfig.decision_variable}
                        onChange={(e) => setNodeConfig({ ...nodeConfig, decision_variable: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* IntentLLM specific fields */}
              {nodeConfig.nodeType === 'IntentLLM' && (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Input Variable
                      </label>
                      <input
                        type="text"
                        value={nodeConfig.input_variable}
                        onChange={(e) => setNodeConfig({ ...nodeConfig, input_variable: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Interactions
                      </label>
                      <input
                        type="number"
                        value={nodeConfig.max_interactions}
                        onChange={(e) => setNodeConfig({ ...nodeConfig, max_interactions: parseInt(e.target.value) })}
                        min={1}
                        max={20}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Output Variable
                      </label>
                      <input
                        type="text"
                        value={nodeConfig.output_variable}
                        onChange={(e) => setNodeConfig({ ...nodeConfig, output_variable: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Intents Variable
                      </label>
                      <input
                        type="text"
                        value={nodeConfig.intents_variable}
                        onChange={(e) => setNodeConfig({ ...nodeConfig, intents_variable: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Intents (separati da virgola)
                    </label>
                    <input
                      type="text"
                      value={nodeConfig.intents?.join(', ')}
                      onChange={(e) => setNodeConfig({
                        ...nodeConfig,
                        intents: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                      })}
                      placeholder="comprare, affittare, vendere, info"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Es: comprare, affittare, vendere, info
                    </p>
                  </div>
                </>
              )}

              {/* LLM_CASE specific fields */}
              {nodeConfig.nodeType === 'llm_case' && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Variable (da valutare)
                    </label>
                    <input
                      type="text"
                      value={nodeConfig.variable}
                      onChange={(e) => setNodeConfig({ ...nodeConfig, variable: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Cases
                      </label>
                      <button
                        onClick={addCase}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                      >
                        <Plus size={14} />
                        Aggiungi Case
                      </button>
                    </div>
                    <div className="space-y-2">
                      {nodeConfig.cases?.map((caseItem, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={caseItem.test}
                            onChange={(e) => updateCase(index, e.target.value)}
                            placeholder="Test"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                          <button
                            onClick={() => removeCase(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Execute Button */}
              <button
                onClick={executeTest}
                disabled={isExecuting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
              >
                {isExecuting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Esecuzione in corso...
                  </>
                ) : (
                  <>
                    <Play size={20} />
                    Esegui Test
                  </>
                )}
              </button>
            </div>
          </div>

          {/* RIGHT COLUMN: Results */}
          <div className="space-y-6">
            {testResult && (
              <>
                {/* Error */}
                {testResult.error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-red-900 mb-2">Errore</h2>
                    <pre className="text-sm text-red-800 whitespace-pre-wrap font-mono">
                      {testResult.error}
                    </pre>
                  </div>
                )}

                {/* Input */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Input Ricevuto</h2>
                  <pre className="bg-gray-50 p-4 rounded text-sm overflow-x-auto font-mono">
                    {JSON.stringify(testResult.input, null, 2)}
                  </pre>
                </div>

                {/* API Call */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">Chiamata API</h2>
                    <button
                      onClick={() => setShowApiDetails(!showApiDetails)}
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                    >
                      {showApiDetails ? <EyeOff size={16} /> : <Eye size={16} />}
                      {showApiDetails ? 'Nascondi' : 'Mostra'}
                    </button>
                  </div>
                  {showApiDetails && (
                    <>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <span className="text-sm font-medium text-gray-700 block mb-1">Endpoint:</span>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded block break-all">
                            {testResult.apiCall.url}
                          </code>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700 block mb-1">Model:</span>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded block">
                            {testResult.apiCall.model}
                          </code>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700 block mb-1">Temperature:</span>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded block">
                            {testResult.apiCall.temperature}
                          </code>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700 block mb-1">Max Tokens:</span>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded block">
                            {testResult.apiCall.max_tokens || 'N/A'}
                          </code>
                        </div>
                      </div>
                      <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
                        <span className="text-sm font-semibold text-blue-900 block mb-2">📦 Payload Completo Inviato:</span>
                        <pre className="bg-white p-3 rounded text-xs overflow-x-auto font-mono max-h-96 overflow-y-auto border border-blue-100">
                          {JSON.stringify({
                            messages: testResult.apiCall.messages,
                            model: testResult.apiCall.model,
                            temperature: testResult.apiCall.temperature,
                            max_tokens: testResult.apiCall.max_tokens
                          }, null, 2)}
                        </pre>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700 mb-2 block">Messages (dettaglio):</span>
                        <pre className="bg-gray-50 p-4 rounded text-sm overflow-x-auto font-mono max-h-64 overflow-y-auto">
                          {JSON.stringify(testResult.apiCall.messages, null, 2)}
                        </pre>
                      </div>
                    </>
                  )}
                </div>

                {/* API Response */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Risposta API (Raw)</h2>
                  <pre className="bg-gray-50 p-4 rounded text-sm overflow-x-auto font-mono max-h-64 overflow-y-auto">
                    {JSON.stringify(testResult.apiResponse, null, 2)}
                  </pre>
                </div>

                {/* Output */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Output Processato</h2>
                  <pre className="bg-gray-50 p-4 rounded text-sm overflow-x-auto font-mono">
                    {JSON.stringify(testResult.output, null, 2)}
                  </pre>
                </div>

                {/* Updated Variables */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Variabili Aggiornate</h2>
                  <pre className="bg-gray-50 p-4 rounded text-sm overflow-x-auto font-mono">
                    {JSON.stringify(testResult.updatedVariables, null, 2)}
                  </pre>
                </div>
              </>
            )}

            {!testResult && !isExecuting && (
              <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
                <Play size={48} className="mx-auto mb-4 opacity-30" />
                <p>Configura il nodo e clicca "Esegui Test" per vedere i risultati</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Prompt Manager Modal */}
      {showPromptManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingPrompt ? 'Modifica Prompt' : 'Gestisci Prompt'}
              </h2>
              <button
                onClick={() => {
                  setShowPromptManager(false);
                  setEditingPrompt(null);
                  setNewPromptName('');
                  setNewPromptContent('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              {/* Create/Edit Form */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">
                  {editingPrompt ? 'Modifica Prompt' : 'Nuovo Prompt'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Prompt
                    </label>
                    <input
                      type="text"
                      value={newPromptName}
                      onChange={(e) => setNewPromptName(e.target.value)}
                      placeholder="Es: Prompt per llm_qualifier - Ice Breaker"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contenuto Prompt
                    </label>
                    <textarea
                      value={newPromptContent}
                      onChange={(e) => setNewPromptContent(e.target.value)}
                      rows={8}
                      placeholder="Inserisci il contenuto del prompt..."
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    {editingPrompt ? (
                      <>
                        <button
                          onClick={handleUpdatePrompt}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          <Save size={16} />
                          Salva Modifiche
                        </button>
                        <button
                          onClick={() => {
                            setEditingPrompt(null);
                            setNewPromptName('');
                            setNewPromptContent('');
                          }}
                          className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                        >
                          Annulla
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleCreatePrompt}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        <Plus size={16} />
                        Salva Nuovo Prompt
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* List of Saved Prompts */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Prompt Salvati ({savedPrompts.length})
                </h3>
                {savedPrompts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>Nessun prompt salvato. Crea il primo prompt qui sopra!</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {savedPrompts.map(prompt => (
                      <div
                        key={prompt.id}
                        className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100"
                      >
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {prompt.name}
                          </h4>
                          <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono max-h-20 overflow-y-auto">
                            {prompt.content.substring(0, 200)}
                            {prompt.content.length > 200 ? '...' : ''}
                          </pre>
                          <p className="text-xs text-gray-400 mt-1">
                            Creato: {new Date(prompt.createdAt).toLocaleString('it-IT')}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleEditPrompt(prompt)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Modifica"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDeletePrompt(prompt.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Elimina"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* List of Saved Nodes */}
              {savedNodes.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    📦 Nodi Salvati ({savedNodes.length})
                  </h3>
                  <div className="space-y-2">
                    {savedNodes.map(node => (
                      <div
                        key={node.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={node.is_visible_in_studio || false}
                              onChange={() => handleToggleVisibility(node.id, node.is_visible_in_studio || false)}
                              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                              title="Visibile in FlowStudio"
                            />
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 mb-1">
                                {node.label}
                              </h4>
                              <p className="text-xs text-gray-600">
                                Tipo: {node.nodeType} | Nome: {node.nodeName}
                                {node.is_visible_in_studio && (
                                  <span className="ml-2 text-green-600 font-semibold">⭐ Visibile in Studio</span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteSavedNode(node.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Elimina nodo"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    💡 Seleziona il checkbox per rendere il nodo visibile nella palette di FlowStudio. I nodi non selezionati rimangono solo in NodeTesting.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NodeTesting;
